import { useParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditVehicleModelForm from "./useEditVehicleModelForm";

export const editVehicleModelPageRouteName = (id?: string) =>
  `/master-data/vehicle-model/${id ?? ":id"}/edit`;

const EditVehicleModelPage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    handleSubmit,
    errors,
    register,
    control,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
    isTruck,
    isFreezerTruck,
    tollClassOptions,
    fuelTypeOptions,
    freezerModelOptions,
    setValue,
  } = useEditVehicleModelForm(id);

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          {/* Vehicle Model Name */}
          <Input
            label={t("label-vehicle-model")}
            placeholder={t("label-vehicle-model")}
            required
            {...register("vehicleModel")}
            error={errors.vehicleModel}
            data-testid="input-vehicle-model-edit"
          />

          {/* Freezer Model - Conditional: truck + is_freezer */}
          {isFreezerTruck && (
            <Controller
              name="freezerModel"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-freezer-model")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={freezerModelOptions}
                    value={field.value}
                    onValueChange={(val) => {
                      field.onChange(val);
                      const option = freezerModelOptions.find((o) => o.value === val);
                      setValue("freezerModelId", option?.id ?? "");
                    }}
                    error={fieldState.error}
                    placeholder={t("label-freezer-model")}
                    data-testid="dropdown-freezer-model-edit"
                  />
                </div>
              )}
            />
          )}

          {/* Fields Row */}
          <div className={`grid gap-4 ${isTruck ? "grid-cols-6" : "grid-cols-4"}`}>
            {/* Tyre Count */}
            <Input
              label={t("label-tyre-count")}
              placeholder={t("label-tyre-count")}
              type="number"
              required
              {...register("tyreCount")}
              error={errors.tyreCount}
              data-testid="input-tyre-count-edit"
            />

            {/* Tyre Size */}
            <Input
              label={t("label-tyre-size")}
              placeholder={t("label-tyre-size")}
              type="text"
              {...register("tyreSize")}
              error={errors.tyreSize}
              data-testid="input-tyre-size-edit"
            />

            {/* Axle Count */}
            <Input
              label={t("label-axle-count")}
              placeholder={t("label-axle-count")}
              type="number"
              required
              {...register("axleCount")}
              error={errors.axleCount}
              data-testid="input-axle-count-edit"
            />

            {/* Default Toll Class */}
            <Controller
              name="tollClass"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-toll-class")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={tollClassOptions}
                    value={field.value}
                    onValueChange={(val) => {
                      field.onChange(val);
                      const option = tollClassOptions.find((o) => o.value === val);
                      setValue("tollClassId", option?.id ?? "");
                    }}
                    error={fieldState.error}
                    placeholder={t("label-toll-class")}
                    data-testid="dropdown-toll-class-edit"
                  />
                </div>
              )}
            />

            {/* Fuel Type - Truck only */}
            {isTruck && (
              <Controller
                name="fuelType"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Typography variant="bodyLarge">
                      {t("label-fuel-type")}{" "}
                      <span className="text-error-500">*</span>
                    </Typography>
                    <Select
                      options={fuelTypeOptions}
                      value={field.value}
                      onValueChange={(val) => {
                        field.onChange(val);
                        const option = fuelTypeOptions.find((o) => o.value === val);
                        setValue("fuelTypeId", option?.id ?? "");
                      }}
                      error={fieldState.error}
                      placeholder={t("label-fuel-type")}
                      data-testid="dropdown-fuel-type-edit"
                    />
                  </div>
                )}
              />
            )}

            {/* Fuel Capacity - Truck only */}
            {isTruck && (
              <Input
                label={t("label-fuel-capacity")}
                placeholder={t("label-fuel-capacity")}
                type="number"
                enableNumberFormat
                {...register("fuelCapacity")}
                error={errors.fuelCapacity}
                data-testid="input-fuel-capacity-edit"
              />
            )}
          </div>
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-model-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-model-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-model-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-model-edit-modal"
      />
    </div>
  );
};

export default EditVehicleModelPage;
